import { useLayoutEffect, useReducer, useRef } from "react"
import { lockedReducer } from "../../utils/defaultReducer"
import { Alert } from "@mui/material"

import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Grid,
    IconButton,
    TextField,
    Tooltip,
    Typography,
    Snackbar,
    Button,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LinkIcon from "@mui/icons-material/Link";
import CodeIcon from "@mui/icons-material/Code";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import useNotification from "../Notification";

export const initialState = {
    embed_url: '',
    popup_url: ''
}

const WebFormsStep3 = ({ parent, setParent }) => {

    const [state, setState] = useReducer(lockedReducer, initialState)

    useLayoutEffect(() => {
        setState(parent)
        return () => {
            setParent(state)
        }
    }, [])

    const linkDireto = state.embed_url;

    const formId = linkDireto.split("/").pop();

    const getDescriptionLineCount = (description = '') => {
        const charsPerLine = 58

        return String(description)
            .split(/\r\n|\r|\n/)
            .reduce((lines, line) => lines + Math.max(1, Math.ceil(line.length / charsPerLine)), 0)
    }

    const calculateDefaultHeight = (formData) => {
        const form = formData?.form
        if (!form) return 600

        const fieldCount = Object.keys(form).length
        const baseHeight = 180
        const fieldHeight = 78
        const extraPadding = 40
        const hasDescriptionField = Object.values(form).some(({ state, type }) => state === 'form' && type === 'description')
        const descriptionLineHeight = 22
        const descriptionExtraHeight = hasDescriptionField
            ? Math.max(getDescriptionLineCount(formData?.description) - 1, 0) * descriptionLineHeight
            : 0

        let estimatedHeight = baseHeight + (fieldCount * fieldHeight) + extraPadding + descriptionExtraHeight

        return Math.min(Math.max(estimatedHeight, 400), 1200)
    };

    const defaultHeightRef = useRef(calculateDefaultHeight(parent))
    const defaultHeight = defaultHeightRef.current

    const embedCode = `<iframe
    id="kwik-form-${formId}"
    src="${linkDireto}"
    style="width:100%; border:none;"
    height="${defaultHeight}px"
    loading="lazy"
    scrolling="no"
    allow="clipboard-write"
></iframe>

<script>
    window.addEventListener("message", function(event) {
        if (event.data?.type !== "kwik-form-height") return;
        if (event.data.formId !== "${formId}") return;
        const iframe = document.getElementById("kwik-form-${formId}");
        if (iframe) iframe.style.height = event.data.height + "px";
    });
</script>`;

    const popupCode = `<script src="${state.popup_url}" async></script>
<button onclick="openKwikForm()" style="
    background:#1976d2;
    color:#fff;
    border:none;
    padding:10px 16px;
    font-size:14px;
    font-weight:600;
    border-radius:6px;
    cursor:pointer;
">
    ABRIR FORMULÁRIO
</button>`;

    const handleCopy = async (text) => {
        try {
            await navigator.clipboard.writeText(text)
            useNotification('Sucesso!', 'Copiado com sucesso', 'success')
        } catch (error) {
            useNotification('Ops!', 'Falha ao copiar', 'danger')
        }
    };

    const isInternal = parent.access_mode === 'internal'

    return (
        <Box sx={{ p: 3, display:'flex', flexDirection: 'column', height: '100%', overflow: 'hidden'}}>
            <Typography variant="h5" sx={{ mb: 2 }}>
                Formas de usar este formulário
            </Typography>

            <Box sx={{display:'flex', flexDirection: 'column', height: '100%', overflow: 'auto', gap:'.5rem'}}>
                {/* LINK DIRETO */}
                <Box>
                    <Card variant="outlined">
                        <CardHeader
                            sx={{padding: '.5rem'}}
                            avatar={<LinkIcon />}
                            title="Link direto"
                            subheader="Compartilhe ou use em botões e menus"
                        />
                        <CardContent sx={{padding: '.5rem !important', display:'flex', gap:'.5rem'}}>
                            <TextField
                                fullWidth
                                value={linkDireto}
                                size="small"
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                        <Tooltip title="Copiar">
                                            <IconButton onClick={() => handleCopy(linkDireto)}>
                                                <ContentCopyIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    ),
                                }}
                            />

                            <Button
                                sx={{width: '16rem'}}
                                variant="outlined"
                                endIcon={<OpenInNewIcon />}
                                href={linkDireto}
                                target="_blank"
                            >
                                Abrir formulário
                            </Button>
                        </CardContent>
                    </Card>
                </Box>

                {isInternal && (
                    <Alert severity="info">
                        Formulários internos exigem login do usuário, então só podem ser usados por link direto
                    </Alert>
                )}

                {!isInternal && (
                <>
                {/* EMBED */}
                <Box  >
                    <Card variant="outlined">
                        <CardHeader
                            sx={{padding: '.5rem'}}
                            avatar={<CodeIcon />}
                            title="Incorporar no site"
                            subheader="Cole no HTML da sua página"
                        />
                        <CardContent  sx={{padding: '.5rem !important'}}>
                            <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                value={embedCode}
                                size="small"
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                        <Tooltip title="Copiar">
                                            <IconButton onClick={() => handleCopy(embedCode)}>
                                                <ContentCopyIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    ),
                                }}
                            />
                        </CardContent>
                    </Card>
                </Box>

                {/* POP-UP */}
                <Box >
                    <Card variant="outlined">
                        <CardHeader
                             sx={{padding: '.5rem'}}
                            avatar={<OpenInNewIcon />}
                            title="Abrir como pop-up"
                            subheader="Adicione este script ao seu site"
                        />
                        <CardContent  sx={{padding: '.5rem !important'}}>
                            <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                value={popupCode}
                                size="small"
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                        <Tooltip title="Copiar">
                                            <IconButton onClick={() => handleCopy(popupCode)}>
                                                <ContentCopyIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    ),
                                }}
                            />
                        </CardContent>
                    </Card>
                </Box>
                </>
                )}
            </Box>
        </Box>
    )
}

export default WebFormsStep3

